import { useEffect, useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import { ImageInput } from "@/components/ImageInput";
import { PRESETS, applyPreset, currentPreset } from "@/lib/presets";

const SECTIONS = [
  {
    title: "Branding",
    note: "Your logo appears in the navbar, footer and backoffice. The favicon shows in the browser tab.",
    fields: [
      { name: "logo_url", label: "Logo Image", type: "image" },
      { name: "favicon_url", label: "Favicon (browser tab icon)", type: "image" },
      { name: "clinic_name", label: "Clinic Name" },
      { name: "tagline", label: "Tagline" },
    ],
  },
  {
    title: "Navigation — Page Names",
    note: "Rename the menu links shown in the header and mobile menu. Leave a field empty to keep its default name. (The pages/URLs stay the same — only the visible label changes.)",
    fields: [
      { name: "nav_label_home", label: "Home link", placeholder: "Home" },
      { name: "nav_label_doctors", label: "Doctors link (was 'About Dr Javeria')", placeholder: "Doctors" },
      { name: "nav_label_treatments", label: "Treatments link", placeholder: "Treatments" },
      { name: "nav_label_results", label: "Results link", placeholder: "Results" },
      { name: "nav_label_journal", label: "Journal link", placeholder: "Journal" },
      { name: "nav_label_contact", label: "Contact link", placeholder: "Contact" },
    ],
  },
  {
    title: "Homepage",
    fields: [
      { name: "hero_video_url", label: "Hero Background Video (mp4/webm — upload your own clinic film)", type: "video" },
      { name: "royal_hero_image", label: "Royal Signature — Hero Portrait (empty = featured treatment image)", type: "image" },
      { name: "royal_hero_mode", label: "Hero Background", type: "select", options: [{ value: "image", label: "Single photo (uses the Hero Portrait above)" }, { value: "rotating", label: "Rotating — cycles through your doctors' photos" }, { value: "none", label: "No photo — clean ivory background" }] },
      { name: "royal_hero_blur", label: "Royal Signature — Hero Photo Blur", type: "select", options: [{ value: "0", label: "Off — crisp photo" }, { value: "4", label: "Soft" }, { value: "10", label: "Medium" }, { value: "16", label: "Dreamy" }] },
      { name: "royal_philosophy_image", label: "Royal Signature — Our Philosophy Image", type: "image" },
      { name: "royal_difference_image", label: "Royal Signature — Royal Difference Image", type: "image" },
      { name: "royal_journey_image", label: "Royal Signature — Treatment Journey Image", type: "image" },
      { name: "hero_eyebrow", label: "Hero Eyebrow (small line above headline)" },
      { name: "hero_title", label: "Hero Headline" },
      { name: "hero_subtitle", label: "Hero Subtitle", type: "textarea" },
      { name: "announcement", label: "Announcement Banner (empty = hidden)" },
      { name: "stat_years", label: "Stat: Years (e.g. 10+)" },
      { name: "stat_patients", label: "Stat: Patients (e.g. 10,000+)" },
      { name: "stat_satisfaction", label: "Stat: Satisfaction (e.g. 99.4%)" },
    ],
  },
  {
    title: "Immersive 3D Design",
    note: "Controls for the Immersive 3D design only — pick its theme and which floating 3D object appears on treatment cards.",
    fields: [
      { name: "imm_theme", label: "Immersive Theme", type: "select", options: [{ value: "dark", label: "Midnight Silk (dark)" }, { value: "light", label: "Pearl Ivory (light)" }] },
      { name: "imm_theme_auto", label: "Auto Day/Night Theme", type: "select", options: [{ value: "off", label: "Off — use the theme picked above" }, { value: "on", label: "On — Pearl Ivory by day (7am–7pm), Midnight Silk by night" }] },
      { name: "charm_hover", label: "Charm Spins On Hover", type: "select", options: [{ value: "on", label: "On — visitors can spin the 3D object with their cursor" }, { value: "off", label: "Off — gentle automatic motion only" }] },
      { name: "hero_scene", label: "Hero Scene Objects", type: "select", options: [{ value: "clinic", label: "Skin clinic objects — IV drip, laser, serums (default)" }, { value: "sculpture", label: "Molten gold sculpture" }] },
      { name: "charm_style", label: "3D Treatment Object", type: "select", options: [{ value: "auto", label: "Auto — match each treatment" }, { value: "syringe", label: "Gold Syringe (injectables)" }, { value: "jar", label: "Cream Jar (skincare)" }, { value: "vial", label: "Gold Vial (IV wellness)" }, { value: "ribbon", label: "Silk Ribbon (hair)" }, { value: "pearl", label: "Pearl (facials)" }, { value: "diamond", label: "Diamond (laser)" }, { value: "ring", label: "Gold Ring (contouring)" }, { value: "drop", label: "Molten Drop" }] },
    ],
  },
  {
    title: "Booking & WhatsApp",
    note: "Slot capacity greys out full time slots per day (default 4). WhatsApp auto-confirmations to visitors activate automatically once all three Twilio keys are saved — the Twilio sandbox sender (whatsapp:+14155238886) works for testing. Until then, visitors get a one-tap WhatsApp confirmation card after booking.",
    fields: [
      { name: "slot_capacity", label: "Slot Capacity (per slot, per day)" },
      { name: "show_prices", label: "Show Prices on Website", type: "select", options: [{ value: "on", label: "On — show treatment & package rates" }, { value: "off", label: "Off — hide all rates (enquiry-led)" }] },
      { name: "twilio_sid", label: "Twilio Account SID" },
      { name: "twilio_token", label: "Twilio Auth Token", type: "password" },
      { name: "twilio_whatsapp_from", label: "Twilio WhatsApp Sender", placeholder: "whatsapp:+14155238886" },
    ],
  },
  {
    title: "Royal Signature Page Heroes",
    note: "Art-direct the cinematic header photo of each page independently. Empty falls back to the page's own content (doctor portrait, latest essay, first result, journey image).",
    fields: [
      { name: "royal_doctors_hero_image", label: "Doctors Page Hero", type: "image" },
      { name: "royal_journal_hero_image", label: "Journal Page Hero", type: "image" },
      { name: "royal_results_hero_image", label: "Results Page Hero", type: "image" },
      { name: "royal_contact_hero_image", label: "Contact Page Hero", type: "image" },
    ],
  },
  {
    title: "Soundscapes",
    note: "The subtle spa soundscape (floating sound button + welcome chime) visitors can enjoy across the site.",
    fields: [
      { name: "sound_enabled", label: "Soundscapes", type: "select", options: [{ value: "on", label: "On — visitors can play the ambient soundscape" }, { value: "off", label: "Off — hide the sound button and mute the welcome chime" }] },
    ],
  },
  {
    title: "Push Notifications",
    note: "Let visitors opt into browser notifications — a push goes out automatically when you publish a new Journal essay. New subscribers instantly receive your welcome offer as a push, turning opt-ins into bookings. Works over HTTPS on visitors' phones and desktops.",
    fields: [
      { name: "push_enabled", label: "Push Notifications", type: "select", options: [{ value: "off", label: "Off" }, { value: "on", label: "On — show the opt-in card to visitors" }] },
      { name: "push_welcome", label: "Welcome Offer Push", type: "select", options: [{ value: "on", label: "On — new subscribers get the welcome gift" }, { value: "off", label: "Off" }] },
      { name: "push_welcome_offer", label: "Welcome Offer Text", placeholder: "10% off your first consultation" },
    ],
  },
  {
    title: "Page Videos (Liquid Luxury)",
    note: "Each Liquid Luxury page opens over a slow, parallaxing ambient clinic film. Stock films are used by default — upload your own mp4/webm here to replace them, or type 'off' to disable video on that page. These only affect the Liquid Luxury design.",
    fields: [
      { name: "video_home", label: "Home — Hero Film", type: "video" },
      { name: "video_treatments", label: "Treatments — Header Film", type: "video" },
      { name: "video_doctors", label: "Doctors — Header Film", type: "video" },
      { name: "video_about", label: "About — Header Film", type: "video" },
      { name: "video_gallery", label: "Gallery — Header Film", type: "video" },
      { name: "video_blog", label: "Journal — Header Film", type: "video" },
      { name: "video_contact", label: "Contact — Header Film", type: "video" },
    ],
  },
  {
    title: "Contact & Location",
    fields: [
      { name: "phone", label: "Phone Number" },
      { name: "whatsapp", label: "WhatsApp Number (digits + country code)" },
      { name: "email", label: "Email Address" },
      { name: "address", label: "Clinic Address", type: "textarea" },
      { name: "hours", label: "Opening Hours" },
      { name: "instagram", label: "Instagram Profile URL" },
      { name: "notification_email", label: "Enquiry Notification Email (where alerts are sent)" },
      { name: "map_embed_url", label: "Google Maps Embed URL" },
      { name: "google_review_url", label: "Google Review Link (from your Google Business Profile → Ask for reviews)" },
    ],
  },
  {
    title: "Instagram Auto-Sync",
    note: "Paste your Instagram Graph API long-lived token and user ID — the homepage feed then updates automatically every 15 minutes. The token is stored securely and never shown on the public site. If left empty, the curated posts from the Instagram tab are shown instead.",
    fields: [
      { name: "instagram_user_id", label: "Instagram User ID (from Graph API /me call)" },
      { name: "instagram_access_token", label: "Long-Lived Access Token", type: "password" },
    ],
  },
];

const DESIGNS = [
  { id: "classic", label: "Serenity Editorial", desc: "Cinematic dark video hero, glass cards, particles & liquid-silk motion", tags: ["Video hero", "Glass UI", "Gold particles"] },
  { id: "liquid", label: "Liquid Luxury", desc: "Editorial magazine layout, Playfair serif, buttery smooth-scroll, cursor aura, velocity blobs & magnetic cards", tags: ["Smooth scroll", "Cursor aura", "Char reveals"] },
  { id: "immersive", label: "Immersive 3D", desc: "Full WebGL experience — molten-gold 3D hero, glass pearls, swirling particle field and a midnight silk theme on every page", tags: ["3D hero", "WebGL particles", "Midnight silk"] },
  { id: "royal", label: "Royal Signature", desc: "Haute editorial — warm ivory canvas, obsidian serif headlines, black small-caps CTAs, before/after results rail and a calm clinical rhythm", tags: ["Editorial", "Ivory & obsidian", "Results rail"] },
];

export const SettingsTab = () => {
  const { content, refresh } = useContent();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [preset, setPreset] = useState(() => currentPreset() || "liquid-gold");
  const [design, setDesign] = useState("classic");

  useEffect(() => {
    if (content?.settings) {
      setForm(content.settings);
      if (content.settings.design_preset && !currentPreset()) setPreset(content.settings.design_preset);
      if (content.settings.design_variant) setDesign(content.settings.design_variant);
    }
  }, [content?.settings]);

  const chooseDesign = (id) => {
    setDesign(id);
    setForm((f) => ({ ...f, design_variant: id }));
  };

  const choosePreset = (id) => {
    setPreset(id);
    setForm((f) => ({ ...f, design_preset: id }));
    applyPreset(id);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", form);
      await refresh();
      toast.success("Site settings saved — live on the website now");
    } catch (e) {
      toast.error("Save failed — please try again");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-settings-tab">
      <h2 className="font-serif text-2xl font-semibold text-[#1A1615]">Site Settings</h2>
      <p className="mt-1 text-sm font-light text-[#91837C]">
        Everything global lives here — design theme, branding, homepage copy, contact details, map and Instagram connection.
      </p>

      <div className="glass mt-6 rounded-3xl p-6 sm:p-8" data-testid="settings-section-website-design">
        <h3 className="font-serif text-xl font-semibold text-[#1A1615]">Website Design</h3>
        <p className="mt-1 text-xs font-light leading-relaxed text-[#91837C]">
          Choose the overall design system for the entire website. Press "Save Settings" below to apply — the change is live instantly. All 5 color themes below work with either design.
        </p>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          {DESIGNS.map((d) => (
            <button
              key={d.id}
              onClick={() => chooseDesign(d.id)}
              data-testid={`settings-design-${d.id}`}
              aria-pressed={design === d.id}
              className={`rounded-2xl border p-6 text-left transition-all duration-500 ease-liquid hover:-translate-y-1 ${
                design === d.id
                  ? "border-[#D4AF37] bg-[#D4AF37]/10 shadow-[0_8px_25px_rgba(212,175,55,0.2)]"
                  : "border-[#E5D9CF] bg-white/60 hover:border-[#D4AF37]/50"
              }`}
            >
              <span className="flex items-center justify-between">
                <span className="font-serif text-xl font-semibold text-[#1A1615]">{d.label}</span>
                {design === d.id && <Check className="h-5 w-5 text-[#B38B2D]" aria-hidden="true" />}
              </span>
              <span className="mt-2 block text-xs font-light leading-relaxed text-[#91837C]">{d.desc}</span>
              <span className="mt-4 flex flex-wrap gap-1.5">
                {d.tags.map((t) => (
                  <span key={t} className="rounded-full bg-[#1A1615] px-2.5 py-1 text-[9px] font-medium uppercase tracking-[0.15em] text-[#F3E5AB]">
                    {t}
                  </span>
                ))}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="glass mt-6 rounded-3xl p-6 sm:p-8" data-testid="settings-section-design-theme">
        <h3 className="font-serif text-xl font-semibold text-[#1A1615]">Design Theme</h3>
        <p className="mt-1 text-xs font-light leading-relaxed text-[#91837C]">
          Pick the default look for every visitor — it previews instantly here. Visitors can still switch themes themselves from the palette icon in the navbar.
        </p>
        <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              onClick={() => choosePreset(p.id)}
              data-testid={`settings-preset-${p.id}`}
              aria-pressed={preset === p.id}
              className={`group rounded-2xl border p-5 text-left transition-all duration-500 ease-liquid hover:-translate-y-1 ${
                preset === p.id
                  ? "border-[#D4AF37] bg-[#D4AF37]/10 shadow-[0_8px_25px_rgba(212,175,55,0.2)]"
                  : "border-[#E5D9CF] bg-white/60 hover:border-[#D4AF37]/50"
              }`}
            >
              <span className="flex items-center justify-between">
                <span className="flex -space-x-2">
                  {p.colors.map((c) => (
                    <span key={c} className="h-6 w-6 rounded-full border-2 border-white shadow" style={{ background: c }} />
                  ))}
                </span>
                {preset === p.id && <Check className="h-4 w-4 text-[#B38B2D]" aria-hidden="true" />}
              </span>
              <span className="mt-4 block font-serif text-lg font-semibold text-[#1A1615]">{p.label}</span>
              <span className="mt-0.5 block text-xs font-light text-[#91837C]">{p.desc}</span>
            </button>
          ))}
        </div>
        <p className="mt-4 text-[11px] font-medium uppercase tracking-[0.15em] text-[#B38B2D]">
          Press "Save Settings" below to make this the site default
        </p>
      </div>

      {SECTIONS.map((sec) => (
        <div key={sec.title} className="glass mt-6 rounded-3xl p-6 sm:p-8" data-testid={`settings-section-${sec.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
          <h3 className="font-serif text-xl font-semibold text-[#1A1615]">{sec.title}</h3>
          {sec.note && <p className="mt-1 text-xs font-light leading-relaxed text-[#91837C]">{sec.note}</p>}
          <div className="mt-5 grid gap-5 sm:grid-cols-2">
            {sec.fields.map((fd) => (
              <div key={fd.name} className={fd.type === "textarea" || fd.type === "image" ? "sm:col-span-2" : ""}>
                <label htmlFor={`setting-${fd.name}`} className="mb-1.5 block text-xs font-medium uppercase tracking-[0.15em] text-[#5A504B]">
                  {fd.label}
                </label>
                {fd.type === "textarea" ? (
                  <textarea
                    id={`setting-${fd.name}`}
                    rows={3}
                    value={form[fd.name] || ""}
                    onChange={(e) => setForm({ ...form, [fd.name]: e.target.value })}
                    data-testid={`settings-field-${fd.name}`}
                    className="input-lux resize-none"
                  />
                ) : fd.type === "image" || fd.type === "video" ? (
                  <ImageInput
                    value={form[fd.name] || ""}
                    onChange={(v) => setForm({ ...form, [fd.name]: v })}
                    testid={`settings-field-${fd.name}`}
                    video={fd.type === "video"}
                  />
                ) : fd.type === "select" ? (
                  <select
                    id={`setting-${fd.name}`}
                    value={form[fd.name] || fd.options?.[0]?.value || ""}
                    onChange={(e) => setForm({ ...form, [fd.name]: e.target.value })}
                    data-testid={`settings-field-${fd.name}`}
                    className="input-lux"
                  >
                    {(fd.options || []).map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    id={`setting-${fd.name}`}
                    type={fd.type === "password" ? "password" : "text"}
                    autoComplete="new-password"
                    placeholder={fd.placeholder || ""}
                    value={form[fd.name] || ""}
                    onChange={(e) => setForm({ ...form, [fd.name]: e.target.value })}
                    data-testid={`settings-field-${fd.name}`}
                    className="input-lux"
                  />
                )}
              </div>
            ))}
          </div>
          {sec.title === "Push Notifications" && form.push_enabled === "on" && (
            <div className="mt-5 border-t border-[#1A1A1A]/8 pt-4">
              <button
                type="button"
                onClick={async () => {
                  try {
                    const { data } = await api.post("/admin/push/test");
                    toast.success(`Test push sent to ${data.sent} subscriber${data.sent === 1 ? "" : "s"}`);
                  } catch {
                    toast.error("Test push failed — check the toggle is saved first");
                  }
                }}
                data-testid="push-test-button"
                className="btn-ghost-lux px-5 py-2.5 text-xs"
              >
                Send test push to subscribers
              </button>
              <p className="mt-2 text-xs font-light text-[#8A7364]">Save settings first, then send a test to everyone who opted in.</p>
            </div>
          )}
        </div>
      ))}

      <button onClick={save} disabled={saving} data-testid="settings-save-button" className="btn-gold mt-6 px-8 py-3.5 text-sm disabled:opacity-60">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
        {saving ? "Saving..." : "Save Settings"}
      </button>
    </div>
  );
};
